/**
 * Shell layout for the medical PA single-order experience (tabs, header, modals, footer).
 *
 * @module singleOrderLayout
 */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AccessDeniedPage from "../../components/accessDeniedPage/accessDeniedPage";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import OrderBlocked from "../../components/orderBlocked/orderBlocked";
import ValidationMessageList from "../../components/validationMessageList/validationMessageList";
import { useBusniessOfficeNotesContext } from "../../context/busniessOfficeNotesContext";
import { OrderFaxLettersProvider } from "../../context/orderFaxLettersContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../enums/modalId";
import { MedicalOrderStatus } from "../../enums/orderReviewStatus";
import { isFcHoldOrg } from "../../enums/organizationTypes";
import { useContainerRect } from "../../hooks/useContainerRect";
import { useOrderAccessPermission } from "../../hooks/useOrderAccessPermission";
import { useOrderBlocked } from "../../hooks/useOrderBlocked";
import { setOpenedModalId } from "../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../store/UserGeneralInfoStore";
import { AgentAiIcon } from "../../svg/agent-ai-icon";
import InProgress from "../../svg/inProgress";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import OrderErrorDisplay from "./components/orderErrorDisplay";
import OrderModals from "./components/orderModals";
import SingleOrderBody from "./components/SingleOrderBody";
import SingleOrderFooter from "./components/SingleOrderFooter";
import SingleOrderHeader from "./components/SingleOrderHeader";
import { useAuthLetterFieldMatchMarkCompleteBlocker } from "./hooks/useAuthLetterFieldMatchMarkCompleteBlocker";
import { useAuthLetterReviewMarkCompleteBlocker } from "./hooks/useAuthLetterReviewMarkCompleteBlocker";
import { useAuthOnFileRefresh } from "./hooks/useAuthOnFileRefresh";
import { useGlobalBoValue } from "./hooks/useGlobalBoValue";
import { useIfMultiplePastRegimenExists } from "./hooks/useIfMultiplePastRegimenExists";
import { useIsPrimaryDiagnosisValidated } from "./hooks/useIsPrimaryDiagnosisValidated";
import { useJCodeStatusCheck } from "./hooks/useJCodeStatusCheck";
import { useMakeTabSticky } from "./hooks/useMakeTabSticky";
import { useOrderComputedValues } from "./hooks/useOrderComputedValues";
import { useOrderDataProcessing } from "./hooks/useOrderDataProcessing";
import { useOrderPermissions } from "./hooks/useOrderPermissions";
import { usePotentialExhaustVisits } from "./hooks/usePotentialExhaustVisits";
import { useReviewDocuments } from "./hooks/useReviewDocuments";
import { useShouldStartFilingAndButtonText } from "./hooks/useShouldStartFilingAndButtonText";
import { useSingleOrderDataLifecycle } from "./hooks/useSingleOrderDataLifecycle";
import { useSingleOrderInitialDataSync } from "./hooks/useSingleOrderInitialDataSync";
import { useSingleOrderUnwrappedData } from "./hooks/useSingleOrderUnwrappedData";
import { useSiteNpiValidated } from "./hooks/useSiteNpiValidated";
import { useSubmissionIsPresent } from "./hooks/useSubmissionIsPresent";
import { useSubmitValidationMessages } from "./hooks/useSubmitValidationMessages";
import { useUpdateBusinessOfficeNotesContext } from "./hooks/useUpdateBusinessOfficeNotesContext";
import { handleStartFilingClick } from "./utils/handleStartFilingClick";
import { openCorrectModal } from "./utils/openCorrectModal";
import {
  logAuthTabSelected,
  logViewTxPlanClicked,
} from "./utils/singleOrderLayoutEventLogger";

/**
 * Top-level layout for a single medical PA order.
 *
 * Orchestrates data fetching, header, regimen info bar, tab navigation,
 * footer actions (start filing / mark as completed), and order modals.
 */
const SingleOrderLayoutContent = (_props: {
  setShowCigNoteModal?: (show: boolean) => void;
}) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = getOrgIdForFetchExternalWorklist();

  const {
    order,
    authOnFile,
    coverages,
    submission,
    clinicalEntries,
    isLoading,
    generalOrderDetailsError,
    fetchAll,
    abortAll,
    fetchAuthOnFileDetails,
  } = useSingleOrderUnwrappedData();

  const { loading: isFormConfigLoading } = useSelector(
    (state: RootState) => state.formConfigNoLiveListen,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const [isApproveEvbvModalOpen, setIsApproveEvbvModalOpen] =
    React.useState<boolean>(false);
  const {
    setGlobalBoValue,
    allCommentsForUI,
    setShouldDisableMarkAsComplete,
    shouldDisableMarkAsComplete,
    hasCptUnsavedChanges,
  } = useBusniessOfficeNotesContext();

  const [imageViewerModalOpen, setImageViewerModalOpen] = useState(false);

  const submissionIsPresent = useSubmissionIsPresent();
  const shouldShowStartFilingButtonAndText =
    useShouldStartFilingAndButtonText();
  const ifMultiplePastRegimenExists = useIfMultiplePastRegimenExists();
  const { hasReviewDocuments } = useReviewDocuments();
  const potentialExhaustVisits = usePotentialExhaustVisits();
  const { hasAccess, errorMessage: accessErrorMessage } =
    useOrderAccessPermission(order, isLoading);
  const isOrderBlocked = useOrderBlocked(order, isLoading);
  const { shouldMakeSticky } = useMakeTabSticky();
  const { containerRef, containerRect } = useContainerRect();
  const { payerName, setPayerName } =
    usePayerNameContextFromSingleOrderLayout();
  useUpdateBusinessOfficeNotesContext();

  const setGlobalBoValueInContext = (orderId: string, data: any) =>
    UserStore.setItem(
      `${UserGeneralInfoContextKeys.GLOBAL_BO_VALUE}_${orderId}`,
      data,
    );
  const { handleAuthOnFileRefresh } = useAuthOnFileRefresh({
    patientMrn: order?.payload?.patient_id,
    drugName: order?.payload?.regimen_name,
    generalOrderDetails: order ?? undefined,
    onSuccess: () => {
      fetchAuthOnFileDetails(orderId ?? "", orgId);
    },
  });
  useJCodeStatusCheck({
    setShouldDisableMarkAsComplete,
  });
  const authLetterReviewBlocker = useAuthLetterReviewMarkCompleteBlocker();
  const authLetterFieldMismatchBlocksMarkComplete =
    useAuthLetterFieldMatchMarkCompleteBlocker();
  const isPrimaryDiagnosisValidated = useIsPrimaryDiagnosisValidated();
  const { isValidationNeeded } = useSiteNpiValidated();
  useOrderDataProcessing();

  const {
    currentStatusBadge,
    tabSwitchElements,
    buttonTextforMarkAsCompleted,
    allMatchedFormConfigs,
    shouldOpenHappyModalValue,
  } = useOrderComputedValues();

  useGlobalBoValue(
    allCommentsForUI,
    boOptions,
    orderId,
    setGlobalBoValue,
    setGlobalBoValueInContext,
  );

  const onBackClick = () => {
    navigate(`/worklists/evbv/${orderId}`);
  };

  const handleOrderBlockedBackClick = () => {
    navigate(`/worklists/`);
  };

  const medicalOrderStatus = order?.status?.medical_order_status;
  const isAgentInProgressOrder =
    medicalOrderStatus === MedicalOrderStatus.AGENT_IN_PROGRESS;

  // FC Hold orgs (OCSRI): matches the backend — completion is only blocked
  // for the four final determinations when the work start (first Trigger RPA,
  // which records the RISA Working stamp and the FC Hold value) is missing.
  const isFcHoldFinalDetermination = [
    MedicalPaOrdersAuthStatus.AuthOnFile,
    MedicalPaOrdersAuthStatus.AuthByRISA,
    MedicalPaOrdersAuthStatus.NoAuthRequired,
    MedicalPaOrdersAuthStatus.DeniedByRISA,
  ].includes(authOnFile?.auth_status as MedicalPaOrdersAuthStatus);
  const fcHoldValueMissing =
    isFcHoldOrg(orgId) &&
    isFcHoldFinalDetermination &&
    !isAgentInProgressOrder &&
    medicalOrderStatus !== MedicalOrderStatus.ORDER_COMPLETED_BY_HUMAN &&
    medicalOrderStatus !== MedicalOrderStatus.ORDER_COMPLETED_BY_AGENT &&
    (!order?.status?.bo_status || !order?.status?.fc_hold_comment);

  const { hideButton, shouldShowReviewButtonValue } = useOrderPermissions();

  const { validationMessages, isSubmitDisabled } = useSubmitValidationMessages({
    shouldShowReviewButton: shouldShowReviewButtonValue,
    hasReviewDocuments,
    hasJCodeNotAvailable: shouldDisableMarkAsComplete,
    authLetterReviewBlocksMarkComplete: authLetterReviewBlocker.blocked,
    authLetterReviewBlockerMessage: authLetterReviewBlocker.message,
    authLetterFieldMismatchBlocksMarkComplete,
    isPrimaryDiagnosisValidated: isPrimaryDiagnosisValidated.isValidated,
    isSiteNpiValidationNeeded: isValidationNeeded,
    hasCptUnsavedChanges,
    fcHoldValueMissing,
  });

  const logOnTabClickEvent = (label: string) => {
    logAuthTabSelected({
      orgId,
      userId: user?.id ?? undefined,
      userEmail: user?.email ?? undefined,
      payerName,
      tabLabel: label,
      generalOrderDetails: order ?? undefined,
      authOnFileDetails: authOnFile ?? undefined,
      order_id: order?.order_id ?? "",
      patient_id: order?.demographics?.patient_id ?? "",
    });
  };

  const handleOpenCorrectModal = () => {
    openCorrectModal({
      generalOrderDetails: order ?? undefined,
      coverageDetails: coverages ?? undefined,
      authOnFileDetails: authOnFile ?? undefined,
      orgId,
      userId: user?.id ?? undefined,
      userEmail: user?.email ?? undefined,
      payerName,
      shouldOpenHappyModalValue,
      dispatch,
    });
  };

  const handleStartFilingClickWrapper = () => {
    handleStartFilingClick({
      orgId,
      userId: user?.id ?? undefined,
      userEmail: user?.email ?? undefined,
      payerName,
      submissionIsPresent,
      allMatchedFormConfigs,
      orderId,
      navigate,
      dispatch,
      generalOrderDetails: order ?? undefined,
      coverageDetails: coverages ?? undefined,
      authOnFileDetails: authOnFile ?? undefined,
    });
  };

  useSingleOrderDataLifecycle({ orderId, orgId, fetchAll, abortAll });
  useSingleOrderInitialDataSync({
    submission,
    authOnFile,
    clinicalEntries,
    orderId,
  });

  const isMainLoading = isLoading || isFormConfigLoading;

  // Gate the full-page loader to the very first load. We only flip the ref off
  // *after* we have actually observed `isMainLoading === true` at least once
  // and then seen it return to false; otherwise an initial render where
  // selectors default to `false` would permanently suppress the loader before
  // the real first fetch begins.
  const hasObservedFirstLoad = useRef<boolean>(false);
  const shouldShowFullPageLoader = useRef<boolean>(true);
  useEffect(() => {
    if (isMainLoading) {
      hasObservedFirstLoad.current = true;
      return;
    }
    if (hasObservedFirstLoad.current && shouldShowFullPageLoader.current) {
      shouldShowFullPageLoader.current = false;
    }
  }, [isMainLoading]);

  useEffect(() => {
    if (coverages) {
      const primaryCoverage = coverages?.find(
        (coverage) => coverage.coverage_type === "primary",
      );
      setPayerName(primaryCoverage?.payer_name ?? "");
    }
  }, [coverages]);

  return (
    <div className="single-order--layout flex h-full w-full flex-col gap-3 !p-0">
      {shouldShowFullPageLoader.current && isMainLoading ? (
        <LoaderMessage message="Fetching Order Details..." />
      ) : generalOrderDetailsError ? (
        <OrderErrorDisplay
          error={generalOrderDetailsError as string}
          onBackClick={onBackClick}
        />
      ) : !hasAccess ? (
        <AccessDeniedPage message={accessErrorMessage ?? undefined} />
      ) : isOrderBlocked ? (
        <OrderBlocked
          icon={
            isAgentInProgressOrder ? (
              <AgentAiIcon fill="#E65100" height={48} width={48} />
            ) : (
              <InProgress height={48} width={48} />
            )
          }
          header={
            isAgentInProgressOrder
              ? "Agent In Progress"
              : "Order Under Development"
          }
          subheader={
            isAgentInProgressOrder
              ? "This order is being processed by an agent. Please proceed with your other orders for now."
              : "This order is currently in development. Please proceed with your other orders for now."
          }
          buttonText="Go Back to Dashboard"
          onClick={handleOrderBlockedBackClick}
        />
      ) : (
        <>
          <div className="single-order-layout--content flex min-h-0 w-full flex-1 flex-col gap-3 overflow-auto rounded-md bg-white px-4 pb-1 pt-2">
            <div className="single-order-layout--content--header shrink-0">
              <SingleOrderHeader
                order={order}
                authOnFile={authOnFile}
                coverages={coverages}
                submission={submission}
                hideButton={hideButton}
                refetchAllData={() => {
                  fetchAll(orderId ?? "", orgId ?? "");
                }}
              />
            </div>
            <SingleOrderBody
              order={order}
              authOnFile={authOnFile}
              ifMultiplePastRegimenExists={ifMultiplePastRegimenExists}
              potentialExhaustVisits={potentialExhaustVisits}
              containerRef={containerRef}
              containerRect={containerRect}
              shouldMakeSticky={shouldMakeSticky}
              tabSwitchElements={tabSwitchElements}
              logOnTabClickEvent={logOnTabClickEvent}
              onAuthOnFileRetry={handleAuthOnFileRefresh}
              onViewTxPlanClick={() => {
                setImageViewerModalOpen(true);
                logViewTxPlanClicked({
                  orgId,
                  userId: user?.id ?? undefined,
                  userEmail: user?.email ?? undefined,
                  payerName,
                  generalOrderDetails: order ?? undefined,
                  authOnFileDetails: authOnFile ?? undefined,
                  order_id: order?.order_id ?? "",
                  patient_id: order?.demographics?.patient_id ?? "",
                });
              }}
              onMedicalNecessityClick={() => {
                if (orderId) {
                  apiWorkerManager.enqueueAndProcess({
                    patient_id: order?.demographics?.patient_id ?? "",
                    order_id: orderId,
                    org_id: orgId ?? "",
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    event_name: EventsName.MEDICAL_NECESSITY_BUTTON_CLICKED,
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
                    },
                  });
                }
                navigate(`/worklists/medical-necessity-analysis/${orderId}`);
              }}
              // Opens the radiology order PDF in the shared DocViewerModal with
              // the oasis toolbar. Path comes from the RPA-captured
              // `radiology_auth_pdf` screenshot entry; the footer is hidden so
              // the modal acts as a read-only preview.
              onClickViewOncoDocument={() => {
                dispatch(
                  setOpenedModalId({
                    id: ModalId.DOC_VIEWER_MODAL,
                    metaData: {
                      filePath:
                        authOnFile?.screenshots?.radiology_auth_pdf
                          ?.screenshot_path,
                      title: "Order Document",
                      hideFooter: true,
                      useOasisToolbar: true,
                    },
                  }),
                );
              }}
              // Opens the order's clinical document in the shared
              // DocViewerModal with the oasis toolbar. Only wired when the
              // order has a `clinical_document.file_path`.
              onClickViewClinicalDocument={
                order?.clinical_document?.file_path
                  ? () => {
                      dispatch(
                        setOpenedModalId({
                          id: ModalId.DOC_VIEWER_MODAL,
                          metaData: {
                            filePath: order?.clinical_document?.file_path,
                            title: "Clinical Document",
                            hideFooter: false,
                            useOasisToolbar: true,
                          },
                        }),
                      );
                    }
                  : undefined
              }
            />
          </div>
          <div className="single-order-layout--footer">
            <SingleOrderFooter
              startFilingButtonText={
                shouldShowStartFilingButtonAndText.startFilingButtonText
              }
              submitButtonText={buttonTextforMarkAsCompleted}
              shouldShowStartFilingButton={
                shouldShowStartFilingButtonAndText.shouldShowStartFilingButton
              }
              shouldShowReviewButton={shouldShowReviewButtonValue}
              hasReviewDocuments={hasReviewDocuments}
              isSubmitDisabled={isSubmitDisabled}
              tooltipContent={
                isSubmitDisabled ? (
                  <ValidationMessageList messages={validationMessages} />
                ) : undefined
              }
              orderId={order?.order_id ?? ""}
              onStartFilingClick={handleStartFilingClickWrapper}
              onSubmitClick={handleOpenCorrectModal}
              onBackClick={onBackClick}
            />
          </div>
        </>
      )}
      <OrderModals
        isApproveEvbvModalOpen={isApproveEvbvModalOpen}
        setIsApproveEvbvModalOpen={setIsApproveEvbvModalOpen}
        openedModalId={openedModalId}
        imageViewerModalOpen={imageViewerModalOpen}
        setImageViewerModalOpen={setImageViewerModalOpen}
        generalOrderDetails={order ?? undefined}
        authOnFileDetails={authOnFile ?? undefined}
        currentStatusBadge={currentStatusBadge}
        authStatusOptions={authStatusOptions}
        userEmail={user?.email ?? ""}
      />
    </div>
  );
};

/** Keep fax review state shared across the document icon and footer without changing AoF data. */
const SingleOrderLayout = (
  props: React.ComponentProps<typeof SingleOrderLayoutContent>,
) => {
  const { orderId } = useParams();
  const orgId = getOrgIdForFetchExternalWorklist();
  return (
    <OrderFaxLettersProvider orderId={orderId ?? ""} orgId={orgId ?? ""}>
      <SingleOrderLayoutContent {...props} />
    </OrderFaxLettersProvider>
  );
};
export default SingleOrderLayout;
